import { PencilSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import ToastifyService, { TOAST_NOTIFY_TYPES } from '../../../../service/ToastifyService';
import { writePendingBirthChart } from '../../../../service/birthDate';
import { getUser, toggleAuthModal, updateUser } from '../../../../store/slices/authSlice';
import {
    closeBirthChartFlow,
    editFlowStep,
    getBirthChartFlow,
    setFlowStep,
    toGenerateBody
} from '../../../../store/slices/birthChartSlice';
import { gaEvents } from '../../../../utils/analytics';
import { toBirthDateTime } from '../../../../utils/birthInfo';
import Button, { BUTTON_STYLE_TYPE } from '../../../common/Button';
import Sheet from './Sheet';
import { formatBirthDate, formatBirthTime, parseBirthDate } from './flowLogic';
import styles from './styles.module.scss';

const Row = ({ label, value, field, onEdit, editLabel }) => (
    <div className={styles.verifyRow}>
        <span className={styles.verifyText}>
            <span className={styles.verifyLabel}>{label}</span> <span className={styles.verifyValue}>{value}</span>
        </span>
        <button type="button" className={styles.pencil} onClick={() => onEdit(field)} aria-label={editLabel}>
            <PencilSimple size={20} color="#ffffff" />
        </button>
    </div>
);

const VerifySheet = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const user = useSelector(getUser);
    const { details, dateOnly, chatId } = useSelector(getBirthChartFlow);
    const [saving, setSaving] = useState(false);

    const onEdit = (step) => dispatch(editFlowStep(step));
    const editLabel = (field) => t('birthChart.flow.verify.edit', { field });

    const time = formatBirthTime(details.birthTime) || '12:00 PM';
    const timeValue = details.birthTimeKnown === false ? t('birthChart.flow.verify.timeUnknown', { time: '12:00 PM' }) : time;
    const city = details.place?.description || '';

    const handleGenerate = () => {
        if (!user?.id) {
            // Anonymous: park the details across the sign-up wall. useAuth resumes at `loading`.
            writePendingBirthChart(toGenerateBody(details));
            gaEvents.bcAuthGateShown();
            dispatch(toggleAuthModal({ trigger: true, context: 'birthChart' }));
            dispatch(closeBirthChartFlow());
            return;
        }
        dispatch(setFlowStep('loading'));
    };

    // Date-only: same write as common/chat/BirthDateModal — deliberately no birthTimeKnown.
    const handleVerifyDate = async () => {
        const parts = parseBirthDate(details.birthDate);
        if (!parts) return;
        setSaving(true);
        try {
            const result = await dispatch(updateUser({ birthDateTime: toBirthDateTime({ ...parts, timeKnown: false }) }));
            if (updateUser.fulfilled.match(result)) {
                dispatch(closeBirthChartFlow());
            } else {
                ToastifyService.setToast(t('birthChart.flow.verify.saveFailed'), TOAST_NOTIFY_TYPES.ERROR);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet
            title={t('birthChart.flow.verify.title')}
            onClose={() => dispatch(closeBirthChartFlow())}
            closeLabel={t('birthChart.flow.close')}
        >
            <div className={styles.verifyRows}>
                <Row
                    label={t('birthChart.flow.verify.date')}
                    value={formatBirthDate(details.birthDate)}
                    field="date"
                    onEdit={onEdit}
                    editLabel={editLabel(t('birthChart.flow.verify.date'))}
                />
                {!dateOnly && (
                    <>
                        <Row
                            label={t('birthChart.flow.verify.time')}
                            value={timeValue}
                            field="time"
                            onEdit={onEdit}
                            editLabel={editLabel(t('birthChart.flow.verify.time'))}
                        />
                        <Row
                            label={t('birthChart.flow.verify.place')}
                            value={city}
                            field="place"
                            onEdit={onEdit}
                            editLabel={editLabel(t('birthChart.flow.verify.place'))}
                        />
                    </>
                )}
            </div>
            <Button
                styleType={BUTTON_STYLE_TYPE.PURPLE}
                extraClasses={[styles.cta]}
                text={dateOnly ? t('birthChart.flow.verify.verify') : t('birthChart.flow.verify.generate')}
                onClick={dateOnly ? handleVerifyDate : handleGenerate}
                loading={saving}
            />
        </Sheet>
    );
};

export default VerifySheet;
